import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { PublicService } from '../services/public.service';
import { SearchCriteriaSubject } from '../subject/search-criteria.subject';
import { CatalogOptionMapper } from '../mappers/catalog-option.mapper';
import { DepartmentVM, DropdownOptionVM, OfferVM, SegmentVM } from '../models/home.vm';
import { SearchCriteria } from '../models/search-criteria.model';

/**
 * Orquesta el buscador hero: catálogos (segmentos, ofertas, departamentos)
 * y la cascada departamento → ciudad → barrio. Al buscar publica el criterio
 * en `SearchCriteriaSubject` (lo consume el marketplace del Slice 1).
 *
 * Reemplaza la orquestación + las 5 inyecciones de `PublicService` que vivían
 * en `RcSearchPropertyComponent`. Se provee a nivel del componente del buscador.
 */
@Injectable()
export class PropertySearchBoxFacade implements OnDestroy {

    private readonly segments = new BehaviorSubject<SegmentVM[]>([]);
    private readonly offers = new BehaviorSubject<OfferVM[]>([]);
    private readonly departments = new BehaviorSubject<DepartmentVM[]>([]);
    private readonly cities = new BehaviorSubject<DropdownOptionVM[]>([]);
    private readonly zones = new BehaviorSubject<DropdownOptionVM[]>([]);
    private readonly subs = new Subscription();

    readonly segments$ = this.segments.asObservable();
    readonly offers$ = this.offers.asObservable();
    readonly departments$ = this.departments.asObservable();
    readonly cities$ = this.cities.asObservable();
    readonly zones$ = this.zones.asObservable();

    constructor(
        private readonly publicService: PublicService,
        private readonly mapper: CatalogOptionMapper,
        private readonly criteriaStore: SearchCriteriaSubject,
    ) {}

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    loadCatalogs(): void {
        this.subs.add(
            this.publicService.getSegmentList().pipe(catchError(() => of([])))
                .subscribe((r) => this.segments.next(this.mapper.toSegments(r))),
        );
        this.subs.add(
            this.publicService.getOffersList(1).pipe(catchError(() => of([])))
                .subscribe((r) => this.offers.next(this.mapper.toOffers(r))),
        );
        this.subs.add(
            this.publicService.getCatalogList('CT_DEPARTMENT').pipe(catchError(() => of([])))
                .subscribe((r) => this.departments.next(this.mapper.toDepartments(r))),
        );
    }

    /** Cascada: al elegir departamento carga sus ciudades y resetea barrios. */
    loadCities(departmentCode: string): void {
        this.zones.next([]);
        this.subs.add(
            this.publicService.getDepartmentCityList(departmentCode).pipe(catchError(() => of([])))
                .subscribe((r) => this.cities.next(this.mapper.toCityOptions(r))),
        );
    }

    loadZones(cityId: any): void {
        this.subs.add(
            this.publicService.getZoneList(cityId).pipe(catchError(() => of([])))
                .subscribe((r) => this.zones.next(this.mapper.toZoneOptions(r))),
        );
    }

    /** Publica el criterio para que el marketplace (Slice 1) lo consuma. */
    submit(criteria: SearchCriteria): void {
        this.criteriaStore.setSearchCriteria(criteria);
    }
}
